import type { IPaymentPlan } from '@/models/paymentPlans';
import dayjs from 'dayjs';
import type { ModelPaymentPlanResponse, ModelPlanPayment } from '@layup/api-sdk';
import type { IPayment, IPaymentReport } from '../models/payments';
import { displayDate, toRandString } from './format.utils';
import { getPaymentStatusRegardlessOfReconciliated } from './payment.utils';
import { nthNumber } from './text.utils';

// DebiCheck mandates only support billing days 1 - 28.
export const MAX_BILLING_DAY_DEBICHECK = 28;
export const MAX_BILLING_DAY = 31;

export const getMaxBillingDay = (debiCheckEnabled?: boolean) =>
  debiCheckEnabled ? MAX_BILLING_DAY_DEBICHECK : MAX_BILLING_DAY;

export const getMonthlyBillingDays = (debiCheckEnabled?: boolean) =>
  Array.from(Array(getMaxBillingDay(debiCheckEnabled)).keys()).map((i) => ({
    value: i + 1,
    text: `${i + 1} ` + nthNumber(i + 1),
  }));

export const determineBillingDay = (billingDay?: number, debiCheckEnabled?: boolean) => {
  const maxBillingDay = getMaxBillingDay(debiCheckEnabled);
  const currentDay = dayjs().date();
  if (billingDay && billingDay >= 1 && billingDay <= maxBillingDay) {
    return billingDay;
  }
  return Math.min(currentDay, maxBillingDay);
};

const getPaymentReport = (payment: ModelPlanPayment): IPaymentReport => {
  let amount = payment.amount || 0;

  if (payment.paymentType === 'REFUND') amount = -amount;

  return {
    id: payment._id!.toString(),
    date: displayDate(payment.dueAt || payment.timestamp),
    type: payment.paymentType ? payment.paymentType.toUpperCase() : '',
    method: payment.connectorData?.paymentMethod ?? '',
    provider: payment.connectorData?.paymentProvider ?? '',
    amount: amount ?? 0,
    status: getPaymentStatusRegardlessOfReconciliated(payment),
  };
};

export const paymentReports = (orderStatus: string, paymentPlan?: ModelPaymentPlanResponse): IPaymentReport[] => {
  if (!paymentPlan || !paymentPlan.payments) return [];

  const sorted = [...paymentPlan.payments]
    .filter((p) => p.dueAt || p.paid)
    .sort((a, b) => dayjs(a.dueAt || a.timestamp).valueOf() - dayjs(b.dueAt || a.timestamp).valueOf());

  const paymentReports = sorted.map(getPaymentReport);
  const filteredReports = paymentReports.filter((payment) => {
    const isInPast = dayjs().isAfter(payment.date);
    const isCurrent = dayjs().isSame(payment.date);
    const isInPastAndPaid = isInPast && payment.status === 'Paid';
    const isNotFailed = payment.status !== 'Failed';

    // Allowing to return payments for cancelled.cancelling order filtering out upcoming payments
    const shouldShowCancelledPayments =
      (orderStatus !== 'CANCELLED' && orderStatus !== 'CANCELLING') || isCurrent || isInPast || isInPastAndPaid;

    return isNotFailed && shouldShowCancelledPayments;
  });
  return filteredReports;
};

export const paymentsRemaining = (paymentPlan: ModelPaymentPlanResponse | IPaymentPlan | undefined): number => {
  if (!paymentPlan || !paymentPlan.quantity) return 0;
  const uppaidPaymentsCount =
    paymentPlan.payments?.filter((p) => !p.failed && !p.paid && (!p.pending || p.pending == null)).length || 0;
  return uppaidPaymentsCount;
};

export const remainingTimeLabel = (plan: ModelPaymentPlanResponse | undefined, isCompleted: boolean): string => {
  if (isCompleted) return '0 months remaining';

  if (!plan?.payments) return '0 months remaining';

  const remaining = Math.max(0, paymentsRemaining(plan));
  const unit = plan.frequency === 'WEEKLY' ? 'week' : 'month';

  return `${remaining} ${remaining > 1 ? `${unit}s` : unit} remaining`;
};

export const percentageComplete = (paymentPlan: ModelPaymentPlanResponse | IPaymentPlan | undefined): number => {
  if (!paymentPlan || !paymentPlan.quantity) return 0;

  let percentage = 0;
  const totalPaid = paymentPlan.payments?.reduce((sum, p) => sum + (p.paid ? p.amount || 0 : 0), 0) || 0;
  const depositDue = paymentPlan.depositDue || 0;
  const amountDue = paymentPlan.amountDue || 0;
  const totalDue = amountDue + depositDue;

  if (paymentPlan.payments && paymentPlan.payments.length > 0) {
    const depositAmount = paymentPlan.payments[0].amount;
    const isDepositFullyPaid = depositAmount === totalDue;

    if (isDepositFullyPaid) {
      percentage = 100;
    } else {
      percentage = (totalPaid / totalDue) * 100;
    }

    if (percentage > 100) percentage = 100;
  }

  return Math.floor(percentage);
};

export const upcomingPayments = (paymentPlan: IPaymentPlan): IPayment[] => {
  if (!paymentPlan) return [];
  const upcomingPayments = paymentPlan.payments?.filter(
    (payment: ModelPlanPayment) => !payment.failed && !payment.paid && !payment.pending,
  );
  if (!upcomingPayments) return [];
  const sortedPayments = upcomingPayments?.sort(
    (a: ModelPlanPayment, b: ModelPlanPayment) =>
      dayjs(a.dueAt || a.timestamp).valueOf() - dayjs(b.dueAt || a.timestamp).valueOf(),
  );
  return sortedPayments;
};

export const nextPayment = (paymentPlan?: ModelPaymentPlanResponse): ModelPlanPayment | null => {
  if (!paymentPlan || !paymentPlan.payments || paymentPlan.payments.length === 0) {
    return null;
  }

  const nextPayment = paymentPlan.payments.find((p: any) => p.due && !p.paid && !dayjs(p.due).isBefore(dayjs()));
  return nextPayment || null;
};

export const planAmount = (paymentPlan?: ModelPaymentPlanResponse): number => {
  if (!paymentPlan) {
    return 0;
  }
  const amountDue = paymentPlan.amountDue || 0;
  const depositDue = paymentPlan.depositDue || 0;
  return amountDue + depositDue;
};

export const duration = (paymentPlan?: ModelPaymentPlanResponse): string => {
  if (paymentPlan && paymentPlan.quantity) {
    const verb = paymentPlan.frequency === 'WEEKLY' ? 'week' : 'month';
    // Add 1 to quantity to account for the deposit payment, giving total duration.
    const totalDuration = paymentPlan.quantity;
    return `${totalDuration} ${totalDuration > 1 ? `${verb}s` : verb}`;
  }
  return '0';
};

export const contributions = (paymentPlan?: ModelPaymentPlanResponse): number => {
  if (!paymentPlan || !paymentPlan.payments) {
    return 0;
  }

  return paymentPlan.payments.reduce((sum, p) => {
    if (p?.paid && p.paymentType !== 'REWARD' && typeof p.amount === 'number') return sum + p.amount;
    return sum;
  }, 0);
};

export const outstanding = (paymentPlan?: ModelPaymentPlanResponse): number => {
  if (!paymentPlan || !paymentPlan.payments) return 0;
  const amountDue = paymentPlan.amountDue || 0;
  const depositDue = paymentPlan.depositDue || 0;
  return Math.max(0, amountDue + depositDue - contributions(paymentPlan));
};

export const generatePaymentsCsv = (payments: IPaymentReport[]): string => {
  const headerTitles = ['ID', 'Payment Date', 'Type', 'Method', 'Provider', 'Amount', 'State'];

  const rows = payments.map((payment) => {
    return [
      payment.id,
      payment.date,
      payment.type,
      payment.method,
      payment.provider,
      toRandString(payment.amount),
      payment.status,
    ]
      .map((value) => `"${String(value).replace(/"/g, '""')}"`)
      .join(',');
  });

  return [headerTitles.join(','), ...rows].join('\n');
};
